import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import {
  ExerciseAssetStatus,
  NotificationExerciseBriefDto,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY } from "../ExercisesTableScreen";
import { INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE } from "./useArchiveRestoreLibraryExercise";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { COACH_EXERCISE_LIBRARY_PENDING_ROUTE } from "../../../routes/routes";
import { updateNotificationCacheOnExerciseApprovalActions } from "./useApprovePendingExerciseLibraryAsset";
import { useUpdateNotificationsCounter } from "../../../hooks/useUpdateCurrentUser";

// Hook for deleting library exercises
// In case of pending exercises, we use exercise notification to check if notification was read before deletion
// If pending exercise notification is not read, we handle it by useUpdateNotificationsCounter
export function useDeleteLibraryExercise(
  deletePendingExercises: boolean = false,
  exerciseNotification?: NotificationExerciseBriefDto,
) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const updateNotificationsCounter = useUpdateNotificationsCounter();

  const isPendingRoute = location.pathname.includes(
    COACH_EXERCISE_LIBRARY_PENDING_ROUTE,
  );

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY],
    mutationFn: ExerciseAssetsService.deleteExerciseAsset,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfExerciseAssetDto, id: number) => {
        return {
          ...oldData,
          items: oldData.items.filter((item) => item.id !== id),
        } as PaginatedListOfExerciseAssetDto;
      },
    },

    exactQueryKey: false,
    options: {
      onSuccess: () => {
        INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });

        if (deletePendingExercises) {
          // if it's pending route no need to invalidate since we are using optimistic updates
          if (!isPendingRoute) {
            queryClient.invalidateQueries({
              queryKey: [
                COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
                {
                  status: ExerciseAssetStatus.PENDING,
                },
              ],
              exact: false,
            });
          }

          updateNotificationCacheOnExerciseApprovalActions(
            queryClient,
            updateNotificationsCounter,
            exerciseNotification,
          );
        }
      },
    },
  });
}
